/**
 * useInlineEditTarget — which property currently shows an input, and how to
 * open, keep, or discard that edit.
 *
 * Exactly one field is open at a time (the CRM table's model), so this is a
 * single key rather than a set. "Commit" only closes the editor: the field's
 * input writes into the draft on every change, so the value is already there —
 * persistence happens later through the card's Save button. Escape is the only
 * path that mutates the draft, restoring the value the field had when it
 * opened.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseInlineEditTargetOptions {
  /** Live view of the draft, used to snapshot a value when its editor opens. */
  editValues: Record<string, unknown>;
  setEditValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  /**
   * Field to open on mount — set when the user entered edit mode by clicking
   * that specific value in the read-only card rather than the pencil.
   */
  initialEditKey?: string | null;
}

export interface InlineEditTarget {
  /** Property whose input is open, or null when every row is read-only. */
  activeEditKey: string | null;
  openEditor: (key: string) => void;
  /** Close, keeping whatever the user typed. */
  commitEditor: () => void;
  /**
   * Close, restoring the value the field had when it opened. Returns the
   * restored draft so the caller can react to it (the autosave re-arms with it,
   * superseding any save armed for the discarded text), or null when no editor
   * was open.
   */
  cancelEditor: () => Record<string, unknown> | null;
  /** Close without touching the draft (used when the whole form resets). */
  closeEditor: () => void;
}

export function useInlineEditTarget({
  editValues,
  setEditValues,
  initialEditKey = null,
}: UseInlineEditTargetOptions): InlineEditTarget {
  const [activeEditKey, setActiveEditKey] = useState<string | null>(null);
  const snapshotRef = useRef<{ key: string; value: unknown } | null>(null);

  // Latest-ref so openEditor can snapshot the current draft without taking
  // editValues as a dependency — otherwise every keystroke would produce a new
  // openEditor and rebuild every diff row. Same render-phase assignment
  // pattern as useActionEditState's discardRef.
  const editValuesRef = useRef(editValues);
  editValuesRef.current = editValues;

  const openEditor = useCallback((key: string) => {
    snapshotRef.current = { key, value: editValuesRef.current[key] };
    setActiveEditKey(key);
  }, []);

  const commitEditor = useCallback(() => {
    snapshotRef.current = null;
    setActiveEditKey(null);
  }, []);

  const cancelEditor = useCallback(() => {
    const snapshot = snapshotRef.current;
    snapshotRef.current = null;
    setActiveEditKey(null);
    if (!snapshot) {
      return null;
    }
    const restored = {
      ...editValuesRef.current,
      [snapshot.key]: snapshot.value,
    };
    editValuesRef.current = restored;
    setEditValues(restored);
    return restored;
  }, [setEditValues]);

  const closeEditor = useCallback(() => {
    snapshotRef.current = null;
    setActiveEditKey(null);
  }, []);

  // Open the field the user clicked in the read-only card.
  const appliedInitialKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (initialEditKey && initialEditKey !== appliedInitialKeyRef.current) {
      appliedInitialKeyRef.current = initialEditKey;
      openEditor(initialEditKey);
    }
  }, [initialEditKey, openEditor]);

  return {
    activeEditKey,
    openEditor,
    commitEditor,
    cancelEditor,
    closeEditor,
  };
}
